import { useRouteLoaderData } from '@remix-run/react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';

import { useDisplayMode } from '~/hooks/useDisplayMode';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';

import Button from '../Button';
import { useEnablePushNotification } from './useEnablePushNotification';

type EnablePushNotificationPromptProps = {
  debug?: boolean;
};

const EnablePushNotificationPrompt = ({ debug = false }: EnablePushNotificationPromptProps) => {
  return <ClientOnly>{() => <EnablePushNotificationPromptImpl debug={debug} />}</ClientOnly>;
};

// biome-ignore lint/correctness/noUnusedVariables: Keep this for now
function EnablePushNotificationPromptImpl({ debug = false }: EnablePushNotificationPromptProps) {
  const isLoggedIn = useIsLoggedIn();
  const displayMode = useDisplayMode();
  const { onEnable } = useEnablePushNotification();
  const { publication } = (useRouteLoaderData('root') as any) ?? {};
  const { t } = useTranslation();

  const [promptShown, setPromptShown] = usePromptShown();

  const [showPrompt, setShowPrompt] = useState(!promptShown && isLoggedIn);

  async function onEnablePushNotification() {
    setShowPrompt(false);
    await onEnable();
    setPromptShown(true);
  }

  function onClose() {
    toast(t('pwa.enable_push_notification_prompt.enable_push_notifications_from_menu'), {
      icon: 'ℹ️',
    });

    setShowPrompt(false);
    setPromptShown(true);
  }

  if (promptShown || displayMode !== 'standalone' || !isLoggedIn) return null;
  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(0,0,0,0.5)]">
      <div className="absolute top-0 w-full rounded-b-xl border bg-white p-6 text-gray-950">
        <div className="flex gap-6">
          <img
            src={publication.logo.thumb.url}
            alt={publication.name}
            className="h-16 w-16 flex-none overflow-clip rounded-lg"
          />
          <div className="flex flex-col gap-6">
            <p>{t('pwa.enable_push_notification_prompt.enable_push_notifications_from_menu')}</p>
            <div className="flex justify-end gap-4">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('pwa.enable_push_notification_prompt.maybe_later')}
              </Button>
              <Button type="button" variant="primary" onClick={onEnablePushNotification}>
                {t('pwa.enable_push_notification_prompt.enable')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function usePromptShown(): [boolean, (value: boolean) => void] {
  let initialPromptShown = false;
  if (typeof document !== 'undefined') {
    initialPromptShown = localStorage.getItem('enablePushNotificationPromptShown') === 'true';
  }
  const [promptShown, setPromptShown] = useState<boolean>(initialPromptShown);

  const setPromptShownAndStore = (value: boolean) => {
    localStorage.setItem('enablePushNotificationPromptShown', String(value));
    setPromptShown(value);
  };

  return [promptShown, setPromptShownAndStore];
}

export default EnablePushNotificationPrompt;
